import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { authFetch } from "@/lib/authFetch";
import {
  Briefcase, Loader2, AlertCircle, Search, ChevronRight,
  TrendingUp, Clock, CheckCircle, AlertTriangle, Euro,
} from "lucide-react";
import { Input } from "@/components/ui/input";

interface Prestation {
  id: string; numero: string; statut: string; typePrestation: string; titre: string;
  niveauUrgence: string | null; clientFinalNom: string | null; siteNom: string | null;
  modeleRobot: string | null; dateIntervention: string | null;
  resellerName: string | null; createdAt: string;
}
interface KpiData {
  total: number; nouvelles: number; enDevis: number; acceptees: number;
  urgentEnCours: number; montantAccepteTtc: number;
}

const STATUTS: Record<string, { label: string; color: string }> = {
  demande_recue: { label: "Demande reçue", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  devis_envoye: { label: "Devis envoyé", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  devis_accepte: { label: "Devis accepté", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  devis_refuse: { label: "Devis refusé", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  intervention_planifiee: { label: "Planifiée", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" },
  intervention_realisee: { label: "Réalisée", color: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400" },
  facture: { label: "Facturée", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" },
  clos: { label: "Clôturée", color: "bg-secondary text-muted-foreground" },
  annule: { label: "Annulée", color: "bg-secondary text-muted-foreground" },
};
const URGENCE_COLORS: Record<string, string> = {
  urgent: "bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400",
  critique: "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400",
};
const KANBAN_COLUMNS = [
  { key: "demande_recue", label: "Nouvelles", borderColor: "border-t-blue-400" },
  { key: "devis_envoye", label: "Devis envoyé", borderColor: "border-t-amber-400" },
  { key: "devis_accepte", label: "Accepté", borderColor: "border-t-green-500" },
  { key: "devis_refuse", label: "Refusé", borderColor: "border-t-red-400" },
  { key: "intervention_planifiee", label: "Planifiée", borderColor: "border-t-purple-400" },
  { key: "intervention_realisee", label: "Réalisée", borderColor: "border-t-teal-400" },
  { key: "facture", label: "Facturée", borderColor: "border-t-orange-400" },
  { key: "clos", label: "Clôturée", borderColor: "border-t-border" },
];

function formatDate(d: string | null) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

type ViewMode = "kanban" | "list";

function KpiCard({ label, value, icon: Icon, color }: { label: string; value: React.ReactNode; icon: any; color: string }) {
  return (
    <div className="bg-card border border-border/50 rounded-xl px-4 py-3 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
        <p className="text-base font-bold text-foreground leading-tight">{value}</p>
      </div>
    </div>
  );
}

export default function SousTraitanceList() {
  const [, navigate] = useLocation();
  const [search, setSearch] = useState("");
  const [view, setView] = useState<ViewMode>("kanban");
  const [statutFilter, setStatutFilter] = useState("all");

  const { data: prestations, isLoading, error } = useQuery<Prestation[]>({
    queryKey: ["/api/prestations", search, statutFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("q", search);
      if (statutFilter !== "all") params.append("statut", statutFilter);
      return authFetch(`/api/prestations?${params}`).then(r => r.json());
    },
  });

  const { data: kpis } = useQuery<KpiData>({
    queryKey: ["/api/prestations/kpis"],
    queryFn: () => authFetch("/api/prestations/kpis").then(r => r.json()),
    staleTime: 30000,
  });

  const filtered = prestations || [];
  const byStatut = KANBAN_COLUMNS.reduce<Record<string, Prestation[]>>((acc, col) => {
    acc[col.key] = filtered.filter(p => p.statut === col.key);
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full">
      {/* KPI Header */}
      {kpis && (
        <div className="px-6 pt-4 pb-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 shrink-0 border-b border-border/40">
          <KpiCard label="Total en cours" value={kpis.total} icon={Briefcase} color="bg-blue-50 text-blue-600 dark:bg-blue-900/20" />
          <KpiCard label="Nouvelles demandes" value={kpis.nouvelles} icon={Clock} color="bg-amber-50 text-amber-600 dark:bg-amber-900/20" />
          <KpiCard label="Devis en attente" value={kpis.enDevis} icon={TrendingUp} color="bg-purple-50 text-purple-600 dark:bg-purple-900/20" />
          <KpiCard label="Urgences actives" value={kpis.urgentEnCours} icon={AlertTriangle} color={kpis.urgentEnCours > 0 ? "bg-red-50 text-red-600 dark:bg-red-900/20" : "bg-secondary text-muted-foreground"} />
          <KpiCard label="CA accepté TTC" value={`${kpis.montantAccepteTtc.toFixed(0)} €`} icon={Euro} color="bg-green-50 text-green-600 dark:bg-green-900/20" />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-border/60 shrink-0 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-muted-foreground" />
          <h1 className="text-base font-semibold text-foreground">Sous-traitance partenaires</h1>
          {prestations && (
            <span className="text-xs bg-secondary text-muted-foreground px-2 py-0.5 rounded-full font-medium">{prestations.length}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input className="pl-8 h-8 w-52 text-sm" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} data-testid="input-search" />
          </div>
          <div className="flex border border-border rounded-lg overflow-hidden">
            <button onClick={() => setView("kanban")} className={`px-3 py-1.5 text-xs transition-colors ${view === "kanban" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary/60"}`} data-testid="btn-view-kanban">Kanban</button>
            <button onClick={() => setView("list")} className={`px-3 py-1.5 text-xs transition-colors ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary/60"}`} data-testid="btn-view-list">Liste</button>
          </div>
        </div>
      </div>

      {/* Statut filter — list mode */}
      {view === "list" && (
        <div className="flex items-center gap-1.5 px-6 py-2.5 border-b border-border/40 flex-wrap shrink-0">
          {[{ key: "all", label: "Toutes" }, ...KANBAN_COLUMNS].map(f => (
            <button key={f.key} onClick={() => setStatutFilter(f.key)}
              className={`px-2.5 py-1 rounded-md text-xs transition-colors ${statutFilter === f.key ? "bg-primary text-primary-foreground font-medium" : "bg-secondary text-muted-foreground hover:text-foreground"}`}
              data-testid={`filter-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>
      )}

      {isLoading && <div className="flex items-center justify-center flex-1 py-20"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div>}
      {error && <div className="p-6"><div className="flex items-center gap-2 text-destructive text-sm p-4 bg-destructive/10 rounded-xl"><AlertCircle className="w-4 h-4" /> Erreur lors du chargement.</div></div>}

      {/* Kanban */}
      {!isLoading && !error && view === "kanban" && (
        <div className="flex-1 overflow-x-auto p-4">
          <div className="flex gap-3 h-full" style={{ minWidth: KANBAN_COLUMNS.length * 240 }}>
            {KANBAN_COLUMNS.map(col => (
              <div key={col.key} className={`flex flex-col flex-none w-56 bg-secondary/30 rounded-xl border-t-2 ${col.borderColor} min-h-0`}>
                <div className="p-3 flex items-center justify-between shrink-0">
                  <span className="text-xs font-semibold text-foreground">{col.label}</span>
                  <span className="text-xs bg-background text-muted-foreground px-1.5 py-0.5 rounded-full">{byStatut[col.key]?.length || 0}</span>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                  {(byStatut[col.key] || []).length === 0 && <p className="text-xs text-muted-foreground text-center py-4">—</p>}
                  {(byStatut[col.key] || []).map(p => (
                    <button key={p.id} onClick={() => navigate(`/sous-traitance/${p.id}`)}
                      className="w-full text-left bg-card border border-border/50 rounded-lg p-3 hover:border-border hover:shadow-sm transition-all space-y-1.5"
                      data-testid={`card-prestation-${p.id}`}>
                      <span className="text-xs font-mono text-muted-foreground">{p.numero}</span>
                      <p className="text-xs font-medium text-foreground line-clamp-2">{p.titre}</p>
                      {p.niveauUrgence && p.niveauUrgence !== "normal" && (
                        <span className={`inline-block text-xs px-1.5 py-0.5 rounded-full font-medium ${URGENCE_COLORS[p.niveauUrgence] || ""}`}>
                          {p.niveauUrgence === "critique" ? "🔴 Critique" : "🟠 Urgent"}
                        </span>
                      )}
                      {p.resellerName && <p className="text-xs text-muted-foreground truncate">{p.resellerName}</p>}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">{p.typePrestation}</span>
                        {formatDate(p.createdAt) && <span className="text-xs text-muted-foreground">{formatDate(p.createdAt)}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Liste */}
      {!isLoading && !error && view === "list" && (
        <div className="flex-1 overflow-y-auto p-4 space-y-1.5">
          {filtered.filter(p => statutFilter === "all" || p.statut === statutFilter).length === 0 && (
            <div className="text-center py-16 text-muted-foreground text-sm">
              <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-30" /> Aucune prestation.
            </div>
          )}
          {filtered.filter(p => statutFilter === "all" || p.statut === statutFilter).map(p => (
            <button key={p.id} onClick={() => navigate(`/sous-traitance/${p.id}`)}
              className="w-full text-left bg-card border border-border/50 rounded-xl p-4 hover:border-border hover:shadow-sm transition-all group flex items-center justify-between gap-4"
              data-testid={`list-prestation-${p.id}`}>
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono text-muted-foreground">{p.numero}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUTS[p.statut]?.color || "bg-secondary text-muted-foreground"}`}>
                    {STATUTS[p.statut]?.label || p.statut}
                  </span>
                  {p.niveauUrgence && p.niveauUrgence !== "normal" && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${URGENCE_COLORS[p.niveauUrgence] || ""}`}>
                      {p.niveauUrgence === "critique" ? "🔴 Critique" : "🟠 Urgent"}
                    </span>
                  )}
                  <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{p.typePrestation}</span>
                </div>
                <p className="font-medium text-sm text-foreground truncate">{p.titre}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {p.resellerName && <span>{p.resellerName}</span>}
                  {p.siteNom && <span>· {p.siteNom}</span>}
                  {p.clientFinalNom && <span>· {p.clientFinalNom}</span>}
                  <span>{formatDate(p.createdAt)}</span>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
